import type { LucideIcon } from 'lucide-react'
import { Avatar, Box, IconButton, Image, Text, VStack } from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import { useCommittedPathname } from '@/components/nav/useCommittedPathname'
import { Bell, Ellipsis, Users } from 'lucide-react'
import { useDispatch, useSelector } from 'react-redux'
import { useGetUnreadCount } from '@/api'
import { useIsRealtimeActive } from '@/lib/ably'
import { selectCurrentUser, selectIsLoggedIn } from '@/store/slices/authSlice'
import { analytics } from '@/lib/analytics'
import { openMenuOverlay } from '@/store/slices/uiSlice'
import { railNavItems, isNavItemActive } from '@/components/nav/navItems'
import { useNavGateBehaviors } from '@/components/nav/useNavGateBehaviors'
import { SignUpPromptModal } from '@/modals/auth'
import { glassCardProps } from '@/styles/glassCard'
import logoImage from '@/assets/images/logo.png'

interface RailRowProps {
  to: string
  icon: LucideIcon
  label: string
  isActive: boolean
  onClick: () => void
  badgeCount?: number
}

/**
 * Shared row treatment for both the content items (`railNavItems`) and the
 * Notifications/Connections cluster (Phase 4a, D61) — one visual/focus/motion
 * recipe rather than two copies that could drift.
 */
function RailRow({ to, icon: Icon, label, isActive, onClick, badgeCount }: RailRowProps) {
  return (
    <Box
      as="button"
      key={to}
      onClick={onClick}
      display="flex"
      alignItems="center"
      gap={3}
      minH="44px"
      px={3}
      py={2}
      borderRadius="md"
      bg="transparent"
      border="none"
      outline="none"
      color={isActive ? 'purple.400' : 'gray.400'}
      filter={isActive ? 'drop-shadow(0 0 6px var(--chakra-colors-purple-400))' : 'none'}
      transition="color 0.15s ease, filter 0.15s ease, background 0.15s ease"
      _hover={{ color: isActive ? 'purple.300' : 'gray.300', bg: 'whiteAlpha.100' }}
      _active={{ transform: 'scale(0.98)' }}
      _focusVisible={{ boxShadow: '0 0 0 2px var(--color-overlay-90), 0 0 0 4px var(--color-primary-purple)' }}
      sx={{
        WebkitTapHighlightColor: 'transparent',
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
    >
      <Box position="relative">
        <Icon size={20} />
        {badgeCount != null && badgeCount > 0 && (
          <Box
            position="absolute"
            top="-6px"
            right="-8px"
            bg="red.500"
            // Intentionally literal: text on a fixed red.500 badge, matching
            // AppHeader's mobile notification badge — needs contrast against
            // that saturated color regardless of app mode.
            color="white"
            borderRadius="full"
            minW="18px"
            h="18px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            // `xs`, not a sub-12px literal — matches AppHeader's mobile
            // notification badge and the locked minimum-font-size floor
            // (scripts/check-font-scale.mjs).
            fontSize="xs"
            fontWeight="bold"
            px="3px"
          >
            {badgeCount > 99 ? '99+' : badgeCount}
          </Box>
        )}
      </Box>
      <Text fontSize="sm" fontWeight={isActive ? 'semibold' : 'normal'}>
        {label}
      </Text>
    </Box>
  )
}

export const DESKTOP_NAV_RAIL_WIDTH = '224px'

/**
 * Fixed 224px desktop left rail — replaces the mobile bottom tab bar at
 * `lg`+ (D45b Phase 2). Content items from `railNavItems` (five, plus the
 * desktop-only History row; no Menu row — reached via the
 * `⋯` trigger below, which opens the Menu overlay once Phase 3 wires it),
 * plus a bottom-pinned profile peek.
 *
 * Mounts as a sibling of `MainNavigationTabBar` for the same route trees
 * (`_authenticated.tsx`, `_publicWithTabs.tsx`), so it shares that
 * component's onboarding-gate check, sign-up prompt, quiz-taking hide, and
 * analytics dispatch via `useNavGateBehaviors` (D45b) —
 * without it, logged-out members and gated members could bypass the mobile
 * tab bar's guardrails simply by using a desktop-width viewport.
 */
export function DesktopNavRail() {
  const pathname = useCommittedPathname()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector(selectCurrentUser)
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const { isQuizTaking, isSignUpModalOpen, setIsSignUpModalOpen, handleNavItemClick } = useNavGateBehaviors()

  // Live unread badge on the Notifications row (Phase 4a, D61) — same
  // real-time-with-polling-fallback pattern as AppHeader's mobile
  // notification icon, so the two surfaces never disagree. Gated on
  // `isLoggedIn` alongside the row it feeds: a logged-out visitor has no
  // unread count to fetch.
  const isRealtimeActive = useIsRealtimeActive()
  const { data: unreadData } = useGetUnreadCount({
    enabled: isLoggedIn,
    refetchInterval: isRealtimeActive ? undefined : 30000,
  })

  const goToProfile = () => navigate({ to: '/profile' })

  const handleRailItemClick = (to: string, label: string, analyticsEvent?: string) => {
    // Per-item analytics fire only once the gate passes, so a logged-out
    // click that just opens the sign-up prompt never reports e.g. a History
    // click. (BOTTOM_NAV_CLICKED still fires inside handleNavItemClick,
    // before the gate, as it does on the mobile tab bar.)
    if (!handleNavItemClick(to, label)) {
      return
    }
    if (analyticsEvent) {
      analytics.capture(analyticsEvent)
    }
    navigate({ to })
  }

  const handleMenuTriggerClick = () => {
    if (!handleNavItemClick('/menu', 'Menu')) {
      return
    }
    dispatch(openMenuOverlay())
  }

  if (isQuizTaking) {
    return null
  }

  return (
    <>
    <SignUpPromptModal
      isOpen={isSignUpModalOpen}
      onClose={() => setIsSignUpModalOpen(false)}
      context="navigation"
    />
    <VStack
      {...glassCardProps}
      as="nav"
      // Excluded from the page-transition root snapshot so the rail stays
      // static during view transitions — see `data-app-chrome` in variables.css.
      data-app-chrome="sidebar"
      position="fixed"
      top="0"
      left="0"
      bottom="0"
      w={DESKTOP_NAV_RAIL_WIDTH}
      zIndex="sticky"
      align="stretch"
      spacing={1}
      py={6}
      px={3}
      borderRadius="0"
      // Opaque page-chrome background, overriding glassCardProps.bg (a
      // ~3.5%-opacity glass tint meant for cards floating over the app's
      // dark canvas). This rail is `position: fixed` chrome sitting over
      // GradientBackground's orb layer, so that near-transparent tint let
      // the orbs bleed through the sidebar. `--color-gray-dark` is the same
      // mode-invariant `#1C1E21` GradientBackground itself paints as its own
      // base fill (variables.css) — using it here makes the rail read as an
      // opaque extension of that always-dark canvas/chrome (the same
      // invariant already documented for header/nav-rail/filter-bar in
      // variables.css's ghost-scrollbar note) rather than a translucent
      // card, and fully occludes the orbs wherever the rail sits. No
      // backdrop-filter needed once the fill is fully opaque — there's
      // nothing behind the rail left to blur/saturate.
      bg="var(--color-gray-dark)"
      backdropFilter="none"
      sx={glassCardProps.sx}
    >
      {/* Paire Logo — moved here from AppHeader on desktop (`lg`+), which
          hides its own copy at that breakpoint. Plain Image, not a button/
          link, matching AppHeader's non-interactive logo treatment. */}
      <Box px={3} mb={4}>
        <Image src={logoImage} height="48px" objectFit="contain" data-testid="desktop-rail-logo" alt="Paire" />
      </Box>

      {railNavItems.map(({ to, icon, label, analyticsEvent }) => (
        <RailRow
          key={to}
          to={to}
          icon={icon}
          label={label}
          isActive={isNavItemActive(to, pathname)}
          onClick={() => handleRailItemClick(to, label, analyticsEvent)}
        />
      ))}

      {/* Notifications/Connections cluster (Phase 4a, D61) — desktop's
          equivalent of AppHeader's mobile notification/friends icons,
          relocated here since the icon row disappears under D61's
          chrome-less header at `lg`+. Logged-in only: mobile has no
          equivalent rows for a logged-out visitor (AppHeader swaps its whole
          icon cluster for Log In / Sign Up buttons), so showing them here
          would give desktop two rows mobile never offers. */}
      {isLoggedIn && (
        <Box mt={1} pt={1} borderTop="1px solid" borderColor="whiteAlpha.100">
          <RailRow
            to="/notifications"
            icon={Bell}
            label="Notifications"
            isActive={isNavItemActive('/notifications', pathname)}
            onClick={() => handleRailItemClick('/notifications', 'Notifications')}
            badgeCount={unreadData?.unreadCount}
          />
          <RailRow
            to="/friends"
            icon={Users}
            label="Connections"
            isActive={isNavItemActive('/friends', pathname)}
            onClick={() => handleRailItemClick('/friends', 'Connections')}
          />
        </Box>
      )}

      {/* Bottom-pinned profile peek */}
      <Box mt="auto" pt={3} borderTop="1px solid" borderColor="whiteAlpha.100">
        <Box
          display="flex"
          alignItems="center"
          gap={3}
          minH="44px"
          px={3}
          py={2}
          borderRadius="md"
          transition="background 0.15s ease"
          _hover={{ bg: 'whiteAlpha.100' }}
        >
          <Box
            as="button"
            onClick={goToProfile}
            display="flex"
            alignItems="center"
            gap={3}
            flex={1}
            minW={0}
            bg="transparent"
            border="none"
            outline="none"
            textAlign="left"
            borderRadius="md"
            // D45b Phase 6 a11y sweep: same missing-focus-indicator gap as
            // the nav items above.
            _focusVisible={{ boxShadow: '0 0 0 2px var(--color-overlay-90), 0 0 0 4px var(--color-primary-purple)' }}
            sx={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Open your profile"
          >
            {/* F24 fix: the row now centers items again (a very long name
                wrapping to 2 lines was previously left top-pinning the
                avatar via flex-start, which misaligned the common
                single-line case). `alignSelf="center"` is still set
                explicitly here even though the parent already centers, so
                the avatar's vertical position doesn't silently depend on
                inheriting alignItems from two ancestors. */}
            <Avatar size="sm" name={user?.name} src={user?.photoUrl || undefined} alignSelf="center" />
            {/* Round-2 fix: the peek was truncating members' names
                ("Yovarni…") — `noOfLines={2}` both wraps AND caps a very
                long name at 2 lines (unbounded wrap could otherwise grow the
                rail's fixed-width row indefinitely). */}
            <Text
              fontSize="sm"
              color="gray.200"
              noOfLines={2}
              wordBreak="break-word"
              lineHeight="short"
            >
              {user?.name ?? 'Profile'}
            </Text>
          </Box>
          <IconButton
            aria-label="More"
            variant="ghost"
            size="sm"
            color="gray.400"
            minW="44px"
            minH="44px"
            flexShrink={0}
            alignSelf="center"
            _hover={{ bg: 'whiteAlpha.200', color: 'gray.200' }}
            onClick={handleMenuTriggerClick}
          >
            <Ellipsis size={18} />
          </IconButton>
        </Box>
      </Box>
    </VStack>
    </>
  )
}

export default DesktopNavRail
